import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Link,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useDeviceAuth } from '../contexts/DeviceAuthContext';
import { COLORS } from '@elevair/shared';
import ErrorMessage from '../components/ErrorMessage';
import { DeviceRegistrationDialog, DevicePendingDialog } from '../components/auth';

// Stricter email validation regex (requires local@domain.tld format)
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .refine((email) => emailRegex.test(email.trim()), { message: 'Please enter a valid email address' }),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login } = useAuth();
  const { fingerprint, deviceToken, isDeviceReady, setDeviceToken } = useDeviceAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<Error | unknown | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Device auth dialogs state
  const [showDeviceRegistration, setShowDeviceRegistration] = useState(false);
  const [showDevicePending, setShowDevicePending] = useState(false);
  const [devicePendingStatus, setDevicePendingStatus] = useState<'pending' | 'inactive' | 'revoked'>('pending');
  const [devicePendingMessage, setDevicePendingMessage] = useState<string | undefined>();
  const [pendingLoginData, setPendingLoginData] = useState<{
    userId: string;
    organizationId: string;
    email: string;
    password: string;
  } | null>(null);

  // Check for session expired query param
  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      setSessionExpired(true);
      // Remove the query param from URL
      searchParams.delete('session');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await login(data.email, data.password, {
        fingerprint: fingerprint || undefined,
        deviceToken: deviceToken || undefined,
      });

      if (result.success) {
        // Save device token if returned
        if (result.deviceToken) {
          setDeviceToken(result.deviceToken);
        }
        navigate('/dashboard');
      } else if (result.requiresDeviceRegistration) {
        // New device - show registration dialog
        setPendingLoginData({
          userId: result.userId!,
          organizationId: result.organizationId!,
          email: data.email,
          password: data.password,
        });
        setShowDeviceRegistration(true);
      } else if (result.deviceStatus) {
        // Device pending/inactive/revoked
        setDevicePendingStatus(result.deviceStatus);
        setDevicePendingMessage(result.message);
        setShowDevicePending(true);
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeviceRegistrationSuccess = () => {
    setShowDeviceRegistration(false);
    setPendingLoginData(null);
    // Show pending dialog since new devices need approval
    setDevicePendingStatus('pending');
    setDevicePendingMessage(
      'Your device has been registered and is awaiting manager approval. You will be able to log in once approved.'
    );
    setShowDevicePending(true);
  };

  const handleDeviceRegistrationCancel = () => {
    setShowDeviceRegistration(false);
    setPendingLoginData(null);
  };

  const handleDevicePendingClose = () => {
    setShowDevicePending(false);
  };

  // Wait for device info to be ready before allowing login
  const isLoginDisabled = isLoading || !isDeviceReady;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: COLORS.background,
      }}
    >
      {/* Header */}
      <Box sx={{ py: 2, px: 3 }}>
        <Typography
          component={RouterLink}
          to="/"
          variant="h5"
          sx={{
            fontFamily: 'Georgia, serif',
            fontWeight: 400,
            color: COLORS.primary,
            textDecoration: 'none',
          }}
        >
          Elevair
        </Typography>
      </Box>

      {/* Login Form */}
      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', py: 4 }}>
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
              Sign in to your Elevair account
            </Typography>

            {sessionExpired && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSessionExpired(false)}>
                Your session has expired due to inactivity. Please sign in again.
              </Alert>
            )}

            {error ? (
              <ErrorMessage
                error={error}
                userFriendlyMessage="Unable to sign in. Please check your credentials and try again."
                onClose={() => setError(null)}
              />
            ) : null}

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <TextField
                {...register('email')}
                label="Email"
                type="text"
                fullWidth
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                autoComplete="email"
                autoFocus
              />

              <TextField
                {...register('password')}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2" underline="hover">
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoginDisabled}
                sx={{ mt: 2, py: 1.5 }}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>

              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3 }}>
                Don&apos;t have an account?{' '}
                <Link component={RouterLink} to="/register" underline="hover">
                  Start your free trial
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Device Registration Dialog */}
      {pendingLoginData && (
        <DeviceRegistrationDialog
          open={showDeviceRegistration}
          userId={pendingLoginData.userId}
          organizationId={pendingLoginData.organizationId}
          onSuccess={handleDeviceRegistrationSuccess}
          onCancel={handleDeviceRegistrationCancel}
        />
      )}

      {/* Device Pending/Inactive/Revoked Dialog */}
      <DevicePendingDialog
        open={showDevicePending}
        status={devicePendingStatus}
        message={devicePendingMessage}
        onClose={handleDevicePendingClose}
      />
    </Box>
  );
}
